import React, { useState } from 'react';

interface EmailTemplatesProps {
  onSave: (settings: any) => Promise<void>;
  isSaving: boolean;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  body: string;
  lastEdited: string;
}

const EmailTemplates: React.FC<EmailTemplatesProps> = ({ onSave, isSaving }) => {
  // Mock templates data
  const initialTemplates: Template[] = [
    {
      id: 'welcome',
      name: 'Welcome Email',
      subject: 'Welcome to {{building_name}}',
      body: '<p>Dear {{recipient_name}},</p><p>Welcome to {{building_name}}. We are delighted to have you as a resident.</p><p>Your account has been set up and you can now access the resident portal using the link below:</p><p><a href="{{login_url}}">Login to Resident Portal</a></p><p>If you have any questions, please don\'t hesitate to contact us.</p><p>Best regards,<br>{{sender_name}}<br>{{building_name}}</p>',
      lastEdited: '2023-01-15',
    },
    {
      id: 'statement',
      name: 'Statement Notification',
      subject: 'New Statement for {{period}} - {{building_name}}',
      body: '<p>Dear {{recipient_name}},</p><p>Your service charge statement for {{period}} is now available.</p><p>Amount Due: {{amount}}<br>Due Date: {{due_date}}</p><p>You can view and download your statement by clicking the link below:</p><p><a href="{{statement_url}}">View Statement</a></p><p>If you have any questions about this statement, please contact us.</p><p>Best regards,<br>{{sender_name}}<br>{{building_name}}</p>',
      lastEdited: '2023-02-20',
    },
    {
      id: 'payment_reminder',
      name: 'Payment Reminder',
      subject: 'Payment Reminder - {{building_name}}',
      body: '<p>Dear {{recipient_name}},</p><p>This is a friendly reminder that your payment of {{amount}} for the period {{period}} is due on {{due_date}}.</p><p>Please ensure that your payment is made on time to avoid any late fees.</p><p>You can view your statement and make a payment by clicking the link below:</p><p><a href="{{statement_url}}">View Statement and Pay</a></p><p>If you have already made your payment, please disregard this reminder.</p><p>Best regards,<br>{{sender_name}}<br>{{building_name}}</p>',
      lastEdited: '2023-03-05',
    },
  ];

  const [templates, setTemplates] = useState<Template[]>(initialTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [editedTemplate, setEditedTemplate] = useState<Template | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setEditedTemplate(template);
    setIsEditing(false);
  };

  const handleEditTemplate = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to original template
    if (selectedTemplate) {
      setEditedTemplate(selectedTemplate);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (editedTemplate) {
      setEditedTemplate({
        ...editedTemplate,
        [name]: value,
      });
    }
  };

  const handleSaveTemplate = async () => {
    if (!editedTemplate) return;
    
    // Update the templates array with the edited template
    const updatedTemplates = templates.map(t => 
      t.id === editedTemplate.id ? {
        ...editedTemplate,
        lastEdited: new Date().toISOString().split('T')[0],
      } : t
    );
    
    setTemplates(updatedTemplates);
    setSelectedTemplate(editedTemplate);
    setIsEditing(false);
    
    // Save to backend
    await onSave({ templates: updatedTemplates });
  };

  return (
    <div className="bg-white shadow-sm rounded-md">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Email Templates</h3>
        <p className="mt-1 text-sm text-gray-500">
          Customize the emails sent to residents and users.
        </p>
      </div>
      
      <div className="flex h-screen max-h-[600px]">
        {/* Template List */}
        <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
          <ul className="divide-y divide-gray-200">
            {templates.map((template) => (
              <li key={template.id}>
                <button
                  className={`w-full px-6 py-4 flex flex-col items-start hover:bg-gray-50 focus:outline-none ${
                    selectedTemplate?.id === template.id ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <span className="text-sm font-medium text-gray-900">{template.name}</span>
                  <span className="mt-1 text-xs text-gray-500">Last edited: {template.lastEdited}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Template Editor */}
        <div className="w-2/3 overflow-y-auto">
          {selectedTemplate ? (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-lg font-medium text-gray-900">{selectedTemplate.name}</h4>
                <div>
                  {isEditing ? (
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveTemplate}
                        disabled={isSaving}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none disabled:opacity-50"
                      >
                        {isSaving ? 'Saving...' : 'Save Template'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleEditTemplate}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      Edit Template
                    </button>
                  )}
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                    Subject Line
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      value={editedTemplate?.subject || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="body" className="block text-sm font-medium text-gray-700">
                    Email Body (HTML)
                  </label>
                  <div className="mt-1">
                    <textarea
                      name="body"
                      id="body"
                      rows={15}
                      value={editedTemplate?.body || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md font-mono disabled:bg-gray-50"
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-md">
                  <h5 className="font-medium text-sm text-gray-700 mb-2">Available Variables</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-xs text-gray-500">
                      <code>{'{{recipient_name}}'}</code> - Recipient's name
                    </div>
                    <div className="text-xs text-gray-500">
                      <code>{'{{building_name}}'}</code> - Building name
                    </div>
                    <div className="text-xs text-gray-500">
                      <code>{'{{sender_name}}'}</code> - Sender's name
                    </div>
                    <div className="text-xs text-gray-500">
                      <code>{'{{period}}'}</code> - Statement period
                    </div>
                    <div className="text-xs text-gray-500">
                      <code>{'{{amount}}'}</code> - Amount due
                    </div>
                    <div className="text-xs text-gray-500">
                      <code>{'{{due_date}}'}</code> - Payment due date
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select a template to edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;
